import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { apiError } from "@/lib/api";

export const PageHeader = ({ title, desc, children }) => (
  <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
    <div>
      <h1 className="text-2xl sm:text-3xl font-extrabold text-white">{title}</h1>
      {desc && <p className="text-slate-400 text-sm mt-1">{desc}</p>}
    </div>
    {children}
  </div>
);

export const Card = ({ children, className = "", ...rest }) => (
  <div className={`rounded-xl bg-[#121620] border border-[#273042] ${className}`} {...rest}>{children}</div>
);

export const Stat = ({ label, value, icon: Icon, color = "#E11D2A" }) => (
  <Card className="p-5">
    <div className="flex items-center justify-between">
      <span className="text-xs uppercase tracking-wider text-slate-400 font-semibold">{label}</span>
      {Icon && <Icon className="w-5 h-5" style={{ color }} />}
    </div>
    <div className="text-3xl font-extrabold text-white mt-2">{value}</div>
  </Card>
);

export const Field = ({ label, ...props }) => (
  <label className="block">
    {label && <span className="text-xs uppercase tracking-wider text-slate-400 font-semibold">{label}</span>}
    <input {...props} className={`mt-1.5 w-full h-11 rounded-xl bg-[#0A0C10] border border-[#273042] px-4 text-white text-sm outline-none focus:border-[#E11D2A]/60 ${props.className || ""}`} />
  </label>
);

export const Btn = ({ children, variant = "primary", className = "", ...props }) => {
  const styles = {
    primary: "bg-[#E11D2A] hover:bg-[#C8121F] text-white btn-glow",
    ghost: "bg-white/5 border border-[#273042] text-white hover:bg-white/10",
    danger: "bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20",
  };
  return <button {...props} className={`h-10 px-4 rounded-lg font-semibold text-sm inline-flex items-center justify-center gap-1.5 transition-colors disabled:opacity-60 ${styles[variant]} ${className}`}>{children}</button>;
};

export const DeleteBtn = ({ onClick, testid }) => (
  <button onClick={onClick} data-testid={testid} className="w-8 h-8 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 flex items-center justify-center transition-colors"><Trash2 className="w-4 h-4" /></button>
);

export const AddBtn = ({ onClick, testid, label = "Add" }) => (
  <Btn onClick={onClick} data-testid={testid}><Plus className="w-4 h-4" /> {label}</Btn>
);

export const Loading = () => <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-[#E11D2A]" /></div>;

export const Empty = ({ text = "Nothing here yet." }) => <div className="text-center py-14 text-slate-500 text-sm">{text}</div>;

// Simple CRUD hook
export function useCrud(fetcher) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await fetcher(); setData(Array.isArray(r.data) ? r.data : r.data?.items || r.data?.campaigns || r.data?.events || r.data?.tickets || r.data?.transactions || []); }
    catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [fetcher]);
  useEffect(() => { load(); }, [load]);
  return { data, loading, reload: load };
}

export const handleErr = (e) => toast.error(apiError(e?.response?.data?.detail));

// Exact timestamp — date + hour + minute + SECOND (24h). Used across all payment/transaction reports.
export const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  });
};
